import React, { FC, useState, useContext } from 'react'
import { Modal, Button, message } from 'antd'
import { useMutation } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import MilkdownEditor from 'components/MilkdownEditor'
import { UPDATE_DOCUMENT_MARKDOWN_MUTATION } from 'graphql/LegalFormalities'
import { LegalDocumentType } from 'types'
import { LegalFormalityDocumentContext } from 'contexts/LegalFormalityDocumentContext'

interface MarkdownEditorModalProps {
  open: boolean
  onClose: () => void
  onSaved?: (document: LegalDocumentType) => void
}

const MarkdownEditorModal: FC<MarkdownEditorModalProps> = ({
  open,
  onClose,
  onSaved,
}) => {
  const { t } = useTranslation()
  const { currentDocument, currentMarkdown, setCurrentMarkdown } = useContext(
    LegalFormalityDocumentContext
  )
  const [saving, setSaving] = useState(false)

  const [updateDocumentMarkdown] = useMutation(
    UPDATE_DOCUMENT_MARKDOWN_MUTATION
  )

  const handleSave = async () => {
    setSaving(true)
    try {
      const response = await updateDocumentMarkdown({
        variables: {
          legalDocumentId: currentDocument.id,
          content: btoa(unescape(encodeURIComponent(currentMarkdown))),
        },
      })
      const result = response.data?.legalFormalityUpdateDocumentMarkdown
      if (result?.error) {
        message.error(result.error)
      } else {
        message.success(
          t('legalFormalities:show.misc.markdownSaved', {
            defaultValue: 'Document sauvegardé et PDF régénéré',
          })
        )
        onSaved?.(result?.legalFormalityDocument)
        onClose()
      }
    } catch (e: any) {
      message.error(e.message || 'Erreur lors de la sauvegarde')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      title={t('legalFormalities:show.misc.editMarkdown', {
        defaultValue: 'Éditer le document',
      })}
      open={open}
      onCancel={onClose}
      width="90vw"
      style={{ top: 20 }}
      bodyStyle={{ height: '75vh', overflow: 'auto' }}
      footer={[
        <Button key="cancel" onClick={onClose}>
          {t('legalFormalities:show.misc.cancel', {
            defaultValue: 'Annuler',
          })}
        </Button>,
        <Button key="save" type="primary" loading={saving} onClick={handleSave}>
          {t('legalFormalities:show.misc.saveAndRegenerate', {
            defaultValue: 'Sauvegarder & Régénérer PDF',
          })}
        </Button>,
      ]}
    >
      {open && currentMarkdown && (
        <MilkdownEditor
          key={`${currentDocument?.id}-${
            currentDocument?.markdownContent?.length ?? 0
          }`}
          defaultValue={currentMarkdown}
          onChange={setCurrentMarkdown}
        />
      )}
      {open && !currentMarkdown && (
        <div style={{ padding: 20, textAlign: 'center', color: '#999' }}>
          {t('legalFormalities:show.misc.noMarkdownContent', {
            defaultValue: 'Aucun contenu markdown disponible pour ce document.',
          })}
        </div>
      )}
    </Modal>
  )
}

export default MarkdownEditorModal
